import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RefreshButton from '../../../components/fields/buttons/refresh-button';
import TableTab from "../../../components/tabs/table-tab";
import { REPORTS_ABSENTEEISM } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import AbsenteeismReportColumn from "../../../models/ColumnDefinitions/AbsenteeismReportColumn";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import AbsenteeismReportSearch from "../../../models/search/AbsenteeismReportSearch";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getAbsenteeismReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { auditRunReport } from "../../../services/auditService";
import { useDialog } from "../../../components/dialog/DialogContext";
import { getErrorMessage } from "../../../services/utilities/ServiceError";

interface AbseenteeismTodayReportProps {
    reportId: string;
}

const AbseenteeismTodayReport: FC<AbseenteeismTodayReportProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const [refreshReport, setRefreshReport] = useState(true);
    const lookup: Lookup[] = [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profileId,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        }
    ];

    useEffect(() => {
        if (refreshReport) {
            dataRefresh(getDataMethod);
        }
        setRefreshReport(false);
    }, [refreshReport])

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_ABSENTEEISM;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, [], reportId) => void) => {
        setDetails(userLoginId, currentMenuDetail, [], reportId);
    }
    // <-- end context

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getAbsenteeismReport((translate.byKey(TranslationKeys.absent)), translate, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }
    
    return (<TableContextProvider
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={AbsenteeismReportColumn}
        searchDescriptor={AbsenteeismReportSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.absenteeism)}
            selectRows={false}
            cyName="abseenteeismTodayReport">
            <RefreshButton onRefresh={setRefreshReport} />
        </TableTab>
    </TableContextProvider >
    );
};

export default AbseenteeismTodayReport;
